import React, { useMemo } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import WindowedSelect from 'react-windowed-select';
import { selectStyles } from '../leaseGeneralInvoiceReceipt/formUtils';
import { MAX, fieldLabels } from './miscReceiptHelpers';

const GRID = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '15px' };
const selectTheme = (theme) => ({ ...theme, borderRadius: 5, padding: 9 });

const FieldError = ({ message }) => (message ? <div className="text-danger small mt-1">{message}</div> : null);

/**
 * Top panel: the money side of the receipt. Everything except Total Amount stays disabled until Total Amount > 0.
 * value: { totalAmount, receiptDate, transactionType, debitAccountCode, chequeNo, drawnOnBank, chequeDate, refNo, narration }
 */
const PaymentPanel = ({
    value, onChange, onTransactionTypeChange, errors = {}, serverError, unlocked, saving,
    transactionTypes, debitAccounts, drawnOnBanks,
}) => {
    const labels = fieldLabels(value.transactionType);
    // Past bank names repeat and can be numerous; keep unique suggestions only.
    const bankSuggestions = useMemo(() => {
        const seen = new Set();
        const out = [];
        drawnOnBanks.forEach((b) => {
            const text = `${b?.text ?? ''}`.trim();
            const key = text.toLowerCase();
            if (text && !seen.has(key)) { seen.add(key); out.push(text); }
        });
        return out;
    }, [drawnOnBanks]);
    // A datalist with hundreds of options is slow to render, so suggest only once the user has typed something.
    const typedBank = `${value.drawnOnBank ?? ''}`.trim().toLowerCase();
    const visibleBankSuggestions = useMemo(
        () => (typedBank.length < 2 ? [] : bankSuggestions.filter((b) => b.toLowerCase().includes(typedBank)).slice(0, 20)),
        [bankSuggestions, typedBank],
    );
    const disabled = !unlocked || saving;
    const debitOptions = useMemo(() => debitAccounts.map((a) => ({ value: a.accountCode, label: `${a.accountCode} - ${a.accountName}` })), [debitAccounts]);
    const typeOptions = useMemo(() => transactionTypes.map((t) => ({ value: t.text, label: t.text })), [transactionTypes]);

    return (
        <div className="border-box2 p-3">
            {serverError && (
                <div className="alert alert-danger py-2" role="alert">{serverError}</div>
            )}
            <div style={GRID}>
                <div>
                    <label className="form-label">Total Amount *</label>
                    <input
                        type="number"
                        min="0"
                        step="0.001"
                        className="p-2 form-control"
                        value={value.totalAmount}
                        disabled={saving}
                        autoFocus
                        onKeyDown={(e) => { if (e.key === '-' || e.key === 'e') e.preventDefault(); }}
                        onChange={(e) => onChange('totalAmount', e.target.value)}
                        onBlur={(e) => { if (e.target.value !== '') onChange('totalAmount', Number(e.target.value).toFixed(3)); }}
                    />
                    <FieldError message={errors.totalAmount} />
                </div>
                <div>
                    <label className="form-label">Receipt Date *</label>
                    <DatePicker
                        selected={value.receiptDate}
                        onChange={(d) => onChange('receiptDate', d)}
                        dateFormat="dd-MMM-yyyy"
                        className="p-2 form-control w-100"
                        todayButton="Today"
                        disabled={disabled}
                    />
                    <FieldError message={errors.receiptDate} />
                </div>
                <div>
                    <label className="form-label">Transaction Type *</label>
                    <WindowedSelect
                        className="p-0"
                        theme={selectTheme}
                        menuPortalTarget={document.body}
                        menuPlacement="auto"
                        styles={selectStyles}
                        isDisabled={disabled}
                        value={value.transactionType ? { value: value.transactionType, label: value.transactionType } : null}
                        onChange={(opt) => onTransactionTypeChange(opt?.value || '')}
                        options={typeOptions}
                    />
                    <FieldError message={errors.transactionType} />
                </div>
                <div>
                    <label className="form-label">Debit Account *</label>
                    <WindowedSelect
                        className="p-0"
                        theme={selectTheme}
                        menuPortalTarget={document.body}
                        menuPlacement="auto"
                        styles={selectStyles}
                        isDisabled={disabled}
                        placeholder="--Select--"
                        value={debitOptions.find((o) => o.value === value.debitAccountCode) || null}
                        onChange={(opt) => onChange('debitAccountCode', opt?.value || '')}
                        options={debitOptions}
                    />
                    <FieldError message={errors.debitAccountCode} />
                </div>
                {labels.chequeNo.visible && (
                    <div>
                        <label className="form-label">{labels.chequeNo.label}</label>
                        <input
                            type="text"
                            className="p-2 form-control"
                            maxLength={MAX.chequeNo}
                            value={value.chequeNo}
                            disabled={disabled}
                            onChange={(e) => onChange('chequeNo', e.target.value)}
                        />
                        <FieldError message={errors.chequeNo} />
                    </div>
                )}
                {labels.drawnOnBank.visible && (
                    <div>
                        <label className="form-label">{labels.drawnOnBank.label}</label>
                        <input
                            type="text"
                            className="p-2 form-control"
                            maxLength={MAX.drawnOnBank}
                            list="miscReceiptDrawnOnBanks"
                            value={value.drawnOnBank}
                            disabled={disabled}
                            onChange={(e) => onChange('drawnOnBank', e.target.value)}
                        />
                        {/* Past values are suggestions only; the field stays free text. */}
                        <datalist id="miscReceiptDrawnOnBanks">
                            {visibleBankSuggestions.map((b, i) => <option key={`${i}-${b}`} value={b} />)}
                        </datalist>
                        <FieldError message={errors.drawnOnBank} />
                    </div>
                )}
                {labels.chequeDate.visible && (
                    <div>
                        <label className="form-label">{labels.chequeDate.label}</label>
                        <DatePicker
                            selected={value.chequeDate}
                            onChange={(d) => onChange('chequeDate', d)}
                            dateFormat="dd-MMM-yyyy"
                            className="p-2 form-control w-100"
                            todayButton="Today"
                            disabled={disabled}
                        />
                        <FieldError message={errors.chequeDate} />
                    </div>
                )}
                <div>
                    <label className="form-label">Ref No</label>
                    <input
                        type="text"
                        className="p-2 form-control"
                        maxLength={MAX.refNo}
                        value={value.refNo}
                        disabled={disabled}
                        onChange={(e) => onChange('refNo', e.target.value)}
                    />
                    <FieldError message={errors.refNo} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label">Narration *</label>
                    <textarea
                        className="p-2 form-control"
                        rows={2}
                        maxLength={MAX.narration}
                        value={value.narration}
                        disabled={disabled}
                        onChange={(e) => onChange('narration', e.target.value)}
                    />
                    <FieldError message={errors.narration} />
                </div>
            </div>
        </div>
    );
};

export default PaymentPanel;
